import React, { useState, useEffect, useCallback } from 'react';
import { staffApi, attendanceApi } from '../api/client';
import { Spin, useFlash } from '../components/UI';
import { Download, Calendar, User } from '../components/Icons';

// ── Constants ─────────────────────────────────────────────────────────────────

const STATUS_OPTIONS = [
  { value: 'present',  label: 'Full Day',  color: 'var(--green)',  bg: 'rgba(22,163,74,0.12)'  },
  { value: 'half_day', label: 'Half Day',  color: '#b45309',       bg: 'rgba(245,158,11,0.12)' },
  { value: 'absent',   label: 'Absent',    color: 'var(--red)',    bg: 'rgba(220,53,69,0.10)'  },
  { value: 'holiday',  label: 'Extra OFF', color: '#7c3aed',       bg: 'rgba(124,58,237,0.10)' },
];

const STATUS_STYLE = {
  present:  { color: 'var(--green)', bg: 'rgba(22,163,74,0.1)',   label: 'Full Day'  },
  half_day: { color: '#b45309',      bg: 'rgba(245,158,11,0.1)',  label: 'Half Day'  },
  absent:   { color: 'var(--red)',   bg: 'rgba(220,53,69,0.08)',  label: 'Absent'    },
  holiday:  { color: '#7c3aed',      bg: 'rgba(124,58,237,0.08)', label: 'Extra OFF' },
};

const MN  = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
const MNF = ['January','February','March','April','May','June','July','August','September','October','November','December'];
const DS  = ['Su','Mo','Tu','We','Th','Fr','Sa'];

// ── Pure helpers ──────────────────────────────────────────────────────────────

const localDateStr = d =>
  `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}-${String(d.getDate()).padStart(2,'0')}`;

const fmtDisp = ds =>
  new Date(ds+'T00:00:00').toLocaleDateString('en-IN', { weekday:'long', year:'numeric', month:'long', day:'numeric' });

const isSun = ds => new Date(ds+'T00:00:00').getDay() === 0;

const fetchRange = async (from, to) => {
  const months = [];
  const cur = new Date(from+'T00:00:00');
  cur.setDate(1);
  const end = new Date(to+'T00:00:00');
  while (cur <= end) {
    months.push({ year: cur.getFullYear(), month: cur.getMonth()+1 });
    cur.setMonth(cur.getMonth()+1);
  }
  const results = await Promise.all(months.map(({ year, month }) => attendanceApi.list({ year, month })));
  return results.flatMap(r => r.data).filter(a => a.date >= from && a.date <= to);
};

// ── PDF helpers ───────────────────────────────────────────────────────────────

const PDF_CSS = `
  *{box-sizing:border-box;margin:0;padding:0}
  body{font-family:Arial,Helvetica,sans-serif;font-size:11px;color:#111;padding:20px}
  .hdr{margin-bottom:14px;padding-bottom:8px;border-bottom:2px solid #1e3a5f}
  .hdr h1{font-size:17px;font-weight:700;color:#1e3a5f;margin-bottom:2px}
  .hdr .sub{font-size:12px;color:#555;font-weight:600;margin-top:2px}
  .hdr .per{font-size:11px;color:#888;margin-top:2px}
  .mo-hd{font-size:13px;font-weight:700;color:#1e3a5f;margin:16px 0 6px}
  table{border-collapse:collapse;width:100%;margin:8px 0}
  th{background:#1e3a5f;color:#fff;font-weight:700;font-size:9px;padding:4px 2px;border:1px solid #1e3a5f;text-align:center}
  td{border:1px solid #ddd;padding:3px 2px;text-align:center;font-size:10px}
  .nm{text-align:left!important;padding-left:6px!important;font-weight:600;min-width:110px}
  .sun{background:#f8f8f8;color:#bbb;border-color:#eee}
  .p{color:#16a34a;font-weight:700}
  .a{color:#dc2626;font-weight:700}
  .h{color:#b45309;font-weight:700}
  .o{color:#7c3aed;font-weight:700}
  .tot td{background:#eef2f7;font-weight:700;border-top:2px solid #1e3a5f}
  .stats{display:flex;gap:12px;margin:12px 0;flex-wrap:wrap}
  .stat{border:1px solid #ddd;border-radius:6px;padding:8px 16px;text-align:center;min-width:90px}
  .stat .v{font-size:22px;font-weight:700}
  .stat .l{font-size:9px;color:#666;margin-top:2px}
  .leg{display:flex;gap:14px;font-size:9px;color:#666;margin-top:10px;flex-wrap:wrap}
  .foot{font-size:9px;color:#aaa;margin-top:14px;border-top:1px solid #eee;padding-top:8px}
  @page{margin:1.5cm;size:A4 landscape}
  @media print{body{padding:0}}
`;

const printWin = (title, body) => {
  const w = window.open('', '_blank', 'width=1000,height=750');
  if (!w) { alert('Please allow popups to download the PDF.'); return; }
  w.document.write(
    `<!DOCTYPE html><html><head><meta charset="utf-8"><title>${title}</title>` +
    `<style>${PDF_CSS}</style></head><body>${body}</body></html>`
  );
  w.document.close();
  w.focus();
  setTimeout(() => w.print(), 600);
};

const buildAllStaffBody = (staffList, data, period) => {
  const map = {};
  data.forEach(a => {
    if (!map[a.staff_id]) map[a.staff_id] = {};
    map[a.staff_id][a.date] = a.status;
  });

  const months = [];
  if (period.mode === 'month') {
    months.push({ year: period.year, month: period.month });
  } else {
    const cur = new Date(period.from+'T00:00:00');
    cur.setDate(1);
    const end = new Date(period.to+'T00:00:00');
    while (cur <= end) {
      months.push({ year: cur.getFullYear(), month: cur.getMonth()+1 });
      cur.setMonth(cur.getMonth()+1);
    }
  }

  const tables = months.map(({ year, month }, mi) => {
    const dim    = new Date(year, month, 0).getDate();
    const mo     = String(month).padStart(2,'0');
    const dFrom  = period.mode === 'range' ? period.from : `${year}-${mo}-01`;
    const dTo    = period.mode === 'range' ? period.to   : `${year}-${mo}-${String(dim).padStart(2,'0')}`;

    const dayNums = [];
    for (let d = 1; d <= dim; d++) {
      const ds = `${year}-${mo}-${String(d).padStart(2,'0')}`;
      if (ds >= dFrom && ds <= dTo) dayNums.push(d);
    }

    const dayHeaders = dayNums.map(d => {
      const ds = `${year}-${mo}-${String(d).padStart(2,'0')}`;
      const sun = isSun(ds);
      return `<th${sun ? ' class="sun"' : ''}>${d}<br/><span style="font-weight:400;font-size:8px">${DS[new Date(ds+'T00:00:00').getDay()]}</span></th>`;
    }).join('');

    let rows = '';
    let totP = 0, totA = 0, totH = 0;
    staffList.forEach(s => {
      let p = 0, a = 0, h = 0;
      const cells = dayNums.map(d => {
        const ds = `${year}-${mo}-${String(d).padStart(2,'0')}`;
        if (isSun(ds)) return '<td class="sun">–</td>';
        const st = map[s.id]?.[ds];
        if (st === 'present')       { p++;          return '<td class="p">P</td>'; }
        if (st === 'half_day')      { h++; p += 0.5; return '<td class="h">H</td>'; }
        if (st === 'absent')        { a++;          return '<td class="a">A</td>'; }
        if (st === 'holiday')                        return '<td class="o">O</td>';
        return '<td style="color:#ddd">·</td>';
      }).join('');
      rows += `<tr><td class="nm">${s.name}</td>${cells}<td class="p">${p}</td><td class="a">${a}</td><td class="h">${h}</td></tr>`;
      totP += p; totA += a; totH += h;
    });
    const blanks = dayNums.map(() => '<td></td>').join('');
    rows += `<tr class="tot"><td class="nm">TOTAL</td>${blanks}<td class="p">${totP}</td><td class="a">${totA}</td><td class="h">${totH}</td></tr>`;

    return (
      `<div style="margin-bottom:20px${mi > 0 ? ';page-break-before:always' : ''}">` +
      (months.length > 1 ? `<div class="mo-hd">${MNF[month-1]} ${year}</div>` : '') +
      `<table><thead><tr><th class="nm">Staff</th>${dayHeaders}<th>P</th><th>A</th><th>H</th></tr></thead><tbody>${rows}</tbody></table></div>`
    );
  });

  return (
    `<div class="hdr"><h1>Sakthi Solar Solutions ERP</h1>` +
    `<div class="sub">Attendance Report — All Staff</div>` +
    `<div class="per">${period.label}</div></div>` +
    tables.join('') +
    `<div class="leg"><span>P = Full Day</span><span>H = Half Day</span><span>A = Absent</span><span>O = Extra OFF</span><span>– = Sunday</span></div>` +
    `<div class="foot">Generated on ${new Date().toLocaleDateString('en-IN',{weekday:'long',year:'numeric',month:'long',day:'numeric'})} | Sakthi Solar Solutions ERP</div>`
  );
};

const buildStaffBody = (staffName, data, period) => {
  const sorted = [...data].sort((a, b) => a.date.localeCompare(b.date));
  let p = 0, a = 0, h = 0, o = 0;
  sorted.forEach(r => {
    if (r.status === 'present')       p++;
    else if (r.status === 'half_day') { h++; p += 0.5; }
    else if (r.status === 'absent')   a++;
    else if (r.status === 'holiday')  o++;
  });
  const rows = sorted.map(r => {
    const d   = new Date(r.date+'T00:00:00');
    const cls = r.status === 'present' ? 'p' : r.status === 'half_day' ? 'h' : r.status === 'absent' ? 'a' : 'o';
    const lbl = r.status === 'present' ? 'Full Day' : r.status === 'half_day' ? 'Half Day' : r.status === 'absent' ? 'Absent' : 'Extra OFF';
    return (
      `<tr>` +
      `<td style="text-align:left;padding-left:6px">${d.toLocaleDateString('en-IN',{year:'numeric',month:'short',day:'numeric'})}</td>` +
      `<td>${d.toLocaleDateString('en-IN',{weekday:'long'})}</td>` +
      `<td class="${cls}">${lbl}</td>` +
      `<td style="text-align:left;padding-left:6px">${r.note || '—'}</td></tr>`
    );
  }).join('');

  return (
    `<div class="hdr"><h1>Sakthi Solar Solutions ERP</h1>` +
    `<div class="sub">Attendance Report — ${staffName}</div>` +
    `<div class="per">${period.label}</div></div>` +
    `<div class="stats">` +
    `<div class="stat"><div class="v p">${p}</div><div class="l">Full Day</div></div>` +
    `<div class="stat"><div class="v h">${h}</div><div class="l">Half Day</div></div>` +
    `<div class="stat"><div class="v a">${a}</div><div class="l">Absent</div></div>` +
    `<div class="stat"><div class="v o">${o}</div><div class="l">Extra OFF</div></div>` +
    `</div>` +
    `<table><thead><tr>` +
    `<th style="text-align:left;padding-left:6px;min-width:100px">Date</th>` +
    `<th style="min-width:100px">Day</th><th>Status</th>` +
    `<th style="text-align:left;padding-left:6px">Note</th>` +
    `</tr></thead><tbody>` +
    (rows || `<tr><td colspan="4" style="padding:14px;text-align:center;color:#aaa">No records found</td></tr>`) +
    `</tbody></table>` +
    `<div class="foot">Generated on ${new Date().toLocaleDateString('en-IN',{weekday:'long',year:'numeric',month:'long',day:'numeric'})} | Sakthi Solar Solutions ERP</div>`
  );
};

// ── Attendance analytics ──────────────────────────────────────────────────────

function computeSummary(staffList, rData) {
  return staffList.map(s => {
    const recs = rData.filter(r => r.staff_id === s.id);
    let p = 0, a = 0, h = 0, o = 0;
    const absentDates = [];
    recs.forEach(r => {
      if (r.status === 'present')       p++;
      else if (r.status === 'half_day') { h++; p += 0.5; }
      else if (r.status === 'absent')   { a++; absentDates.push(r.date); }
      else if (r.status === 'holiday')  o++;
    });
    const total = recs.length;
    const attendancePct = total > 0 ? (p / total) * 100 : null;

    // consecutive absent days
    const sorted = [...absentDates].sort();
    let maxRun = 0, curRun = 0;
    sorted.forEach((d, i) => {
      if (i === 0) { curRun = 1; maxRun = 1; return; }
      const prev = new Date(sorted[i-1]+'T00:00:00');
      const curr = new Date(d+'T00:00:00');
      const gap  = (curr - prev) / 86400000;
      curRun = gap === 1 ? curRun + 1 : 1;
      if (curRun > maxRun) maxRun = curRun;
    });

    // absence events = separate runs of 1+ absent days
    let events = 0, inRun = false;
    sorted.forEach((d, i) => {
      if (i === 0) { events = 1; inRun = true; return; }
      const gap = (new Date(d+'T00:00:00') - new Date(sorted[i-1]+'T00:00:00')) / 86400000;
      if (gap > 1) events++;
    });

    const monAbsent = absentDates.filter(d => new Date(d+'T00:00:00').getDay() === 1).length;
    const friAbsent = absentDates.filter(d => new Date(d+'T00:00:00').getDay() === 5).length;

    return { id: s.id, name: s.name, role: s.role, p, a, h, o, total, attendancePct, maxRun, events: a > 0 ? events : 0, monAbsent, friAbsent };
  }).filter(s => s.total > 0);
}

const FLAG_COLORS = {
  high:   { bg: 'rgba(220,53,69,0.08)',   border: 'rgba(220,53,69,0.3)',   text: 'var(--red)',   dot: '#dc2626' },
  medium: { bg: 'rgba(245,158,11,0.09)',  border: 'rgba(245,158,11,0.35)', text: '#b45309',      dot: '#f59e0b' },
  low:    { bg: 'rgba(124,58,237,0.07)',  border: 'rgba(124,58,237,0.25)', text: '#7c3aed',      dot: '#7c3aed' },
};

function AttendanceSummary({ staffList, rData }) {
  const stats = computeSummary(staffList, rData);
  if (stats.length === 0) return null;

  const sorted = [...stats].sort((a, b) => b.a - a.a);
  const maxAbsent = sorted[0]?.a || 0;

  // build flags
  const flags = [];
  stats.forEach(s => {
    const absRate = s.total > 0 ? (s.a / s.total) * 100 : 0;
    if (s.attendancePct !== null && s.attendancePct < 75)
      flags.push({ name: s.name, severity: 'high',   msg: `Attendance rate ${s.attendancePct.toFixed(1)}% — below 75%` });
    if (absRate > 15 && s.a >= 2)
      flags.push({ name: s.name, severity: 'high',   msg: `${s.a} absences — ${absRate.toFixed(1)}% of working days` });
    if (s.maxRun >= 3)
      flags.push({ name: s.name, severity: 'high',   msg: `${s.maxRun} consecutive absent days` });
    else if (s.maxRun === 2)
      flags.push({ name: s.name, severity: 'medium', msg: `2 consecutive absent days` });
    if (s.events >= 3 && s.a >= 3)
      flags.push({ name: s.name, severity: 'medium', msg: `${s.events} separate absence instances — frequent pattern` });
    if (s.monAbsent >= 2)
      flags.push({ name: s.name, severity: 'medium', msg: `${s.monAbsent} Monday absences — possible weekend-extension pattern` });
    if (s.friAbsent >= 2)
      flags.push({ name: s.name, severity: 'medium', msg: `${s.friAbsent} Friday absences — possible weekend-extension pattern` });
    if (s.h >= 4)
      flags.push({ name: s.name, severity: 'low',    msg: `${s.h} half-days` });
  });

  // highlight cards
  const topAbsentee  = sorted[0];
  const lowestRate   = [...stats].filter(s => s.attendancePct !== null).sort((a, b) => a.attendancePct - b.attendancePct)[0];
  const mostFrequent = [...stats].sort((a, b) => b.events - a.events)[0];
  const mostConsec   = [...stats].sort((a, b) => b.maxRun - a.maxRun)[0];

  const cardStyle = (color) => ({
    flex: '1 1 180px', borderRadius: 10, padding: '14px 16px',
    background: color === 'red'    ? 'rgba(220,53,69,0.07)'
              : color === 'amber'  ? 'rgba(245,158,11,0.08)'
              : color === 'violet' ? 'rgba(124,58,237,0.07)'
              : 'rgba(30,58,95,0.06)',
    border: `1px solid ${
      color === 'red'    ? 'rgba(220,53,69,0.2)'
    : color === 'amber'  ? 'rgba(245,158,11,0.25)'
    : color === 'violet' ? 'rgba(124,58,237,0.2)'
    : 'rgba(30,58,95,0.15)'}`,
  });

  return (
    <div className="card" style={{ marginTop: 16, padding: '18px 20px' }}>
      <div style={{ fontWeight: 700, fontSize: 14, color: 'var(--navy)', marginBottom: 16, display: 'flex', alignItems: 'center', gap: 8 }}>
        <span style={{ fontSize: 16 }}>📊</span> Attendance Insights
      </div>

      {/* Highlight cards */}
      <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', marginBottom: 20 }}>
        {maxAbsent > 0 && (
          <div style={cardStyle('red')}>
            <div style={{ fontSize: 10, fontWeight: 700, color: 'var(--red)', textTransform: 'uppercase', letterSpacing: '.5px', marginBottom: 6 }}>Most Absences</div>
            <div style={{ fontWeight: 700, fontSize: 16, color: 'var(--navy)' }}>{topAbsentee.name}</div>
            <div style={{ fontSize: 13, color: 'var(--red)', fontWeight: 600, marginTop: 2 }}>{topAbsentee.a} absent day{topAbsentee.a !== 1 ? 's' : ''}</div>
            <div style={{ fontSize: 11, color: 'var(--text2)', marginTop: 3 }}>{topAbsentee.attendancePct?.toFixed(1)}% attendance rate</div>
          </div>
        )}
        {mostFrequent && mostFrequent.events >= 2 && (
          <div style={cardStyle('amber')}>
            <div style={{ fontSize: 10, fontWeight: 700, color: '#b45309', textTransform: 'uppercase', letterSpacing: '.5px', marginBottom: 6 }}>Most Frequent Leaves</div>
            <div style={{ fontWeight: 700, fontSize: 16, color: 'var(--navy)' }}>{mostFrequent.name}</div>
            <div style={{ fontSize: 13, color: '#b45309', fontWeight: 600, marginTop: 2 }}>{mostFrequent.events} separate instances</div>
            <div style={{ fontSize: 11, color: 'var(--text2)', marginTop: 3 }}>{mostFrequent.a} total absent days</div>
          </div>
        )}
        {mostConsec && mostConsec.maxRun >= 2 && (
          <div style={cardStyle('red')}>
            <div style={{ fontSize: 10, fontWeight: 700, color: 'var(--red)', textTransform: 'uppercase', letterSpacing: '.5px', marginBottom: 6 }}>Longest Absence Streak</div>
            <div style={{ fontWeight: 700, fontSize: 16, color: 'var(--navy)' }}>{mostConsec.name}</div>
            <div style={{ fontSize: 13, color: 'var(--red)', fontWeight: 600, marginTop: 2 }}>{mostConsec.maxRun} consecutive days</div>
          </div>
        )}
        {lowestRate && lowestRate.attendancePct !== null && (
          <div style={cardStyle('amber')}>
            <div style={{ fontSize: 10, fontWeight: 700, color: '#b45309', textTransform: 'uppercase', letterSpacing: '.5px', marginBottom: 6 }}>Lowest Attendance Rate</div>
            <div style={{ fontWeight: 700, fontSize: 16, color: 'var(--navy)' }}>{lowestRate.name}</div>
            <div style={{ fontSize: 13, color: '#b45309', fontWeight: 600, marginTop: 2 }}>{lowestRate.attendancePct.toFixed(1)}%</div>
            <div style={{ fontSize: 11, color: 'var(--text2)', marginTop: 3 }}>{lowestRate.a} absent · {lowestRate.h} half-day</div>
          </div>
        )}
      </div>

      {/* Red flags */}
      {flags.length > 0 && (
        <div style={{ marginBottom: 20 }}>
          <div style={{ fontWeight: 700, fontSize: 12, color: 'var(--text2)', textTransform: 'uppercase', letterSpacing: '.5px', marginBottom: 10 }}>
            ⚠ Red Flags ({flags.length})
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 7 }}>
            {flags.map((f, i) => {
              const fc = FLAG_COLORS[f.severity];
              return (
                <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 10, background: fc.bg, border: `1px solid ${fc.border}`, borderRadius: 8, padding: '8px 14px' }}>
                  <span style={{ width: 8, height: 8, borderRadius: '50%', background: fc.dot, flexShrink: 0 }}/>
                  <span style={{ fontWeight: 700, color: 'var(--navy)', fontSize: 13, minWidth: 110 }}>{f.name}</span>
                  <span style={{ fontSize: 12, color: fc.text, fontWeight: 500 }}>{f.msg}</span>
                  <span style={{ marginLeft: 'auto', fontSize: 10, fontWeight: 700, padding: '2px 8px', borderRadius: 10, background: fc.dot, color: '#fff', textTransform: 'uppercase' }}>{f.severity}</span>
                </div>
              );
            })}
          </div>
        </div>
      )}
      {flags.length === 0 && (
        <div style={{ background: 'rgba(22,163,74,0.07)', border: '1px solid rgba(22,163,74,0.2)', borderRadius: 8, padding: '10px 16px', fontSize: 13, color: 'var(--green)', fontWeight: 500, marginBottom: 20 }}>
          ✓ No red flags — attendance looks healthy for this period.
        </div>
      )}

      {/* Detailed breakdown table */}
      <div style={{ fontWeight: 700, fontSize: 12, color: 'var(--text2)', textTransform: 'uppercase', letterSpacing: '.5px', marginBottom: 8 }}>Staff Breakdown</div>
      <div style={{ overflowX: 'auto' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
          <thead>
            <tr style={{ background: 'var(--surface)' }}>
              <th style={{ textAlign: 'left', padding: '7px 10px', fontWeight: 600, color: 'var(--text2)' }}>Staff</th>
              <th style={{ textAlign: 'center', padding: '7px 8px', fontWeight: 600, color: 'var(--green)' }}>Present</th>
              <th style={{ textAlign: 'center', padding: '7px 8px', fontWeight: 600, color: '#b45309' }}>Half-day</th>
              <th style={{ textAlign: 'center', padding: '7px 8px', fontWeight: 600, color: 'var(--red)' }}>Absent</th>
              <th style={{ textAlign: 'center', padding: '7px 8px', fontWeight: 600, color: 'var(--text2)' }}>Rate %</th>
              <th style={{ textAlign: 'center', padding: '7px 8px', fontWeight: 600, color: 'var(--text2)' }}>Instances</th>
              <th style={{ textAlign: 'center', padding: '7px 8px', fontWeight: 600, color: 'var(--text2)' }}>Max Streak</th>
              <th style={{ textAlign: 'center', padding: '7px 8px', fontWeight: 600, color: 'var(--text2)' }}>Mon ✗</th>
              <th style={{ textAlign: 'center', padding: '7px 8px', fontWeight: 600, color: 'var(--text2)' }}>Fri ✗</th>
            </tr>
          </thead>
          <tbody>
            {sorted.map(s => {
              const absRate = s.total > 0 ? (s.a / s.total) * 100 : 0;
              const rateColor = s.attendancePct === null ? 'var(--text2)' : s.attendancePct < 75 ? 'var(--red)' : s.attendancePct < 85 ? '#b45309' : 'var(--green)';
              return (
                <tr key={s.id} style={{ borderBottom: '1px solid var(--border)' }}>
                  <td style={{ padding: '7px 10px', fontWeight: 600 }}>{s.name}</td>
                  <td style={{ textAlign: 'center', color: 'var(--green)', fontWeight: 700 }}>{s.p}</td>
                  <td style={{ textAlign: 'center', color: '#b45309', fontWeight: 700 }}>{s.h || '—'}</td>
                  <td style={{ textAlign: 'center', color: s.a > 0 ? 'var(--red)' : 'var(--text2)', fontWeight: 700 }}>{s.a || '—'}</td>
                  <td style={{ textAlign: 'center', fontWeight: 700, color: rateColor }}>
                    {s.attendancePct !== null ? s.attendancePct.toFixed(1) + '%' : '—'}
                  </td>
                  <td style={{ textAlign: 'center', color: s.events >= 3 ? '#b45309' : 'var(--text2)' }}>
                    {s.events > 0 ? s.events : '—'}
                  </td>
                  <td style={{ textAlign: 'center', color: s.maxRun >= 3 ? 'var(--red)' : s.maxRun >= 2 ? '#b45309' : 'var(--text2)' }}>
                    {s.maxRun > 0 ? s.maxRun : '—'}
                  </td>
                  <td style={{ textAlign: 'center', color: s.monAbsent >= 2 ? '#b45309' : 'var(--text2)' }}>
                    {s.monAbsent > 0 ? s.monAbsent : '—'}
                  </td>
                  <td style={{ textAlign: 'center', color: s.friAbsent >= 2 ? '#b45309' : 'var(--text2)' }}>
                    {s.friAbsent > 0 ? s.friAbsent : '—'}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      <div style={{ marginTop: 10, fontSize: 11, color: 'var(--text2)', lineHeight: 1.8 }}>
        <strong>Instances</strong> = separate leave events &nbsp;|&nbsp;
        <strong>Max Streak</strong> = longest consecutive absent days &nbsp;|&nbsp;
        <strong>Mon/Fri ✗</strong> = absences on Monday/Friday
      </div>
    </div>
  );
}

// ── Sub-components ────────────────────────────────────────────────────────────

const ErrorBanner = ({ msg, onRetry }) => (
  <div style={{ background:'rgba(220,53,69,0.08)', border:'1px solid rgba(220,53,69,0.25)', borderRadius:10, padding:'14px 18px', marginBottom:16, display:'flex', alignItems:'center', gap:12 }}>
    <span style={{ color:'var(--red)', fontWeight:600, flex:1, fontSize:14 }}>{msg}</span>
    {onRetry && <button className="btn btn-sm" onClick={onRetry}>Retry</button>}
  </div>
);

function MonthGrid({ staff, dataMap, year, month }) {
  const dim = new Date(year, month, 0).getDate();
  const mo  = String(month).padStart(2,'0');
  return (
    <div className="card card-0">
      <div style={{ overflowX:'auto' }}>
        <table style={{ minWidth:900 }}>
          <thead>
            <tr>
              <th style={{ minWidth:130, position:'sticky', left:0, background:'var(--card)', zIndex:1, textAlign:'left', padding:'8px 12px' }}>Staff</th>
              {Array.from({ length:dim }, (_,i) => i+1).map(d => {
                const ds  = `${year}-${mo}-${String(d).padStart(2,'0')}`;
                const sun = isSun(ds);
                return (
                  <th key={d} style={{ textAlign:'center', fontSize:11, minWidth:36, padding:'4px 2px', background:sun?'#f3f4f6':undefined, color:sun?'#aaa':undefined }}>
                    <div>{d}</div>
                    <div style={{ fontSize:9, fontWeight:400 }}>{DS[new Date(ds+'T00:00:00').getDay()]}</div>
                  </th>
                );
              })}
              <th style={{ textAlign:'center' }}>Present</th>
              <th style={{ textAlign:'center' }}>Absent</th>
            </tr>
          </thead>
          <tbody>
            {staff.map(s => {
              let pCount = 0, aCount = 0;
              const cells = Array.from({ length:dim }, (_,i) => i+1).map(d => {
                const ds  = `${year}-${mo}-${String(d).padStart(2,'0')}`;
                const sun = isSun(ds);
                if (sun) return { ds, sun:true };
                const st = dataMap[s.id]?.[ds];
                if (st === 'present')  pCount++;
                else if (st === 'half_day') pCount += 0.5;
                else if (st === 'absent')   aCount++;
                return { ds, st };
              });
              return (
                <tr key={s.id}>
                  <td style={{ fontWeight:600, fontSize:13, position:'sticky', left:0, background:'var(--card)', zIndex:1, padding:'6px 12px' }}>{s.name}</td>
                  {cells.map(({ ds, sun, st }) => {
                    if (sun) return <td key={ds} style={{ background:'#f3f4f6', textAlign:'center', fontSize:10, color:'#bbb' }}>–</td>;
                    const sty = st ? STATUS_STYLE[st] : null;
                    return (
                      <td key={ds} style={{ textAlign:'center', padding:'3px 2px' }}>
                        {sty
                          ? <span title={sty.label} style={{ display:'block', margin:'0 auto', width:22, height:22, borderRadius:'50%', background:sty.bg, color:sty.color, fontSize:10, fontWeight:700, lineHeight:'22px' }}>
                              {st==='present'?'P':st==='half_day'?'H':st==='absent'?'A':'O'}
                            </span>
                          : <span style={{ color:'#ddd', fontSize:11 }}>·</span>
                        }
                      </td>
                    );
                  })}
                  <td style={{ textAlign:'center', fontWeight:700, color:'var(--green)' }}>{pCount}</td>
                  <td style={{ textAlign:'center', fontWeight:700, color:'var(--red)' }}>{aCount}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      <div style={{ display:'flex', gap:14, padding:'10px 16px', borderTop:'1px solid var(--border)', flexWrap:'wrap' }}>
        {[['P','var(--green)','rgba(22,163,74,0.1)','Full Day'],['H','#b45309','rgba(245,158,11,0.1)','Half Day'],['A','var(--red)','rgba(220,53,69,0.08)','Absent'],['O','#7c3aed','rgba(124,58,237,0.08)','Extra OFF']].map(([l,c,bg,lbl]) => (
          <span key={l} style={{ display:'flex', alignItems:'center', gap:5, fontSize:12 }}>
            <span style={{ width:20, height:20, borderRadius:'50%', background:bg, color:c, fontWeight:700, fontSize:10, lineHeight:'20px', textAlign:'center' }}>{l}</span>
            {lbl}
          </span>
        ))}
        <span style={{ fontSize:12, color:'#aaa' }}>– = Sunday Off &nbsp; · = Not marked</span>
      </div>
    </div>
  );
}

// ── Main component ────────────────────────────────────────────────────────────

export default function Attendance() {
  const today = localDateStr(new Date());
  const { show, Flash } = useFlash();

  // Day view
  const [staff, setStaff]           = useState([]);
  const [date, setDate]             = useState(today);
  const [attendance, setAtt]        = useState({});
  const [notes, setNotes]           = useState({});
  const [saving, setSaving]         = useState(false);
  const [busy, setBusy]             = useState(true);
  const [loadError, setLoadError]   = useState(null);

  // Navigation
  const [view, setView]             = useState('day'); // 'day' | 'month' | 'report'

  // Month view
  const nowD = new Date(date+'T00:00:00');
  const [viewMonth, setViewMonth]   = useState(nowD.getMonth()+1);
  const [viewYear, setViewYear]     = useState(nowD.getFullYear());
  const [monthData, setMonthData]   = useState([]);
  const [monthBusy, setMB]          = useState(false);
  const [monthError, setMonthError] = useState(null);

  // Report
  const td = new Date();
  const [rTab, setRTab]   = useState('all');    // 'all' | 'staff'
  const [rMode, setRMode] = useState('month');  // 'month' | 'range'
  const [rYear, setRYear] = useState(td.getFullYear());
  const [rMonth, setRMonth] = useState(td.getMonth()+1);
  const [rFrom, setRFrom] = useState(`${td.getFullYear()}-${String(td.getMonth()+1).padStart(2,'0')}-01`);
  const [rTo, setRTo]     = useState(today);
  const [rStaff, setRStaff] = useState('');
  const [rData, setRData]   = useState([]);
  const [rBusy, setRBusy]   = useState(false);
  const [rError, setRError] = useState(null);
  const [rDone, setRDone]   = useState(false);

  // ── Loaders ──

  const loadDay = useCallback(async d => {
    setBusy(true); setLoadError(null);
    try {
      const [{ data: st }, { data: att }] = await Promise.all([
        staffApi.list(),
        attendanceApi.list({ date: d }),
      ]);
      setStaff(st.filter(s => s.is_active && s.role !== 'superadmin'));
      const map = {}, nm = {};
      att.forEach(a => { map[a.staff_id] = a.status; nm[a.staff_id] = a.note || ''; });
      setAtt(map); setNotes(nm);
    } catch {
      setLoadError('Failed to load attendance data. Your session may have expired — please refresh or log in again.');
    } finally { setBusy(false); }
  }, []);

  useEffect(() => { loadDay(date); }, [date, loadDay]);

  const loadMonth = useCallback(async (y, m) => {
    setMB(true); setMonthError(null);
    try {
      const { data } = await attendanceApi.list({ year: y, month: m });
      setMonthData(data);
    } catch {
      setMonthError('Failed to load monthly data. Your session may have expired.');
    } finally { setMB(false); }
  }, []);

  useEffect(() => { if (view === 'month') loadMonth(viewYear, viewMonth); }, [view, viewYear, viewMonth, loadMonth]);

  const loadReport = async () => {
    if (rMode === 'range' && rFrom > rTo) { setRError('From date must be before or equal to To date.'); return; }
    if (rTab === 'staff' && !rStaff)      { setRError('Please select a staff member.'); return; }
    setRBusy(true); setRError(null); setRDone(false);
    try {
      let data;
      if (rMode === 'month') {
        const { data: d } = await attendanceApi.list({ year: rYear, month: rMonth });
        data = d;
      } else {
        data = await fetchRange(rFrom, rTo);
      }
      setRData(data); setRDone(true);
    } catch { setRError('Failed to load report data. Please try again.'); }
    finally  { setRBusy(false); }
  };

  // ── Day view handlers ──

  const setStatus = (sid, status) => setAtt(a => ({ ...a, [sid]: status }));
  const setNote   = (sid, val)    => setNotes(n => ({ ...n, [sid]: val }));

  const shiftDate = delta => {
    const d = new Date(date+'T00:00:00');
    d.setDate(d.getDate()+delta);
    setDate(localDateStr(d));
  };

  const save = async () => {
    const sunday  = isSun(date);
    const records = staff.map(s => ({ staff_id:s.id, status:sunday?'off':(attendance[s.id]||'present'), note:notes[s.id]||null }));
    setSaving(true);
    try   { await attendanceApi.bulkMark({ date, records }); show('Attendance saved!'); }
    catch { show('Failed to save attendance.', 'error'); }
    finally { setSaving(false); }
  };

  // ── Report helpers ──

  const getPeriod = () => {
    if (rMode === 'month') return { mode:'month', year:rYear, month:rMonth, label:`${MNF[rMonth-1]} ${rYear}` };
    const fl = new Date(rFrom+'T00:00:00').toLocaleDateString('en-IN',{year:'numeric',month:'short',day:'numeric'});
    const tl = new Date(rTo+'T00:00:00').toLocaleDateString('en-IN',{year:'numeric',month:'short',day:'numeric'});
    return { mode:'range', from:rFrom, to:rTo, label:`${fl} – ${tl}` };
  };

  const handleDownloadAllPDF   = () => printWin('Attendance — All Staff',   buildAllStaffBody(staff, rData, getPeriod()));
  const handleDownloadStaffPDF = () => {
    const sm = staff.find(s => s.id === rStaff);
    if (sm) printWin(`Attendance — ${sm.name}`, buildStaffBody(sm.name, rData.filter(r => r.staff_id === rStaff), getPeriod()));
  };

  // ── Computed ──

  const monthMap = {};
  monthData.forEach(a => { if (!monthMap[a.staff_id]) monthMap[a.staff_id] = {}; monthMap[a.staff_id][a.date] = a.status; });

  const rMap = {};
  rData.forEach(a => { if (!rMap[a.staff_id]) rMap[a.staff_id] = {}; rMap[a.staff_id][a.date] = a.status; });

  const sunday         = isSun(date);
  const selStaffMember = staff.find(s => s.id === rStaff);

  if (busy && view === 'day') return <Spin/>;

  return (
    <div>
      <Flash/>
      <div className="page-hd">
        <h2>Attendance</h2>
        <div style={{ display:'flex', gap:8 }}>
          <button className={`btn btn-sm${view==='day'?' btn-primary':''}`}    onClick={() => setView('day')}>Day View</button>
          <button className={`btn btn-sm${view==='month'?' btn-primary':''}`}  onClick={() => setView('month')}>Monthly View</button>
          <button className={`btn btn-sm${view==='report'?' btn-primary':''}`} onClick={() => setView('report')}>Reports</button>
        </div>
      </div>

      {/* ── Day View ──────────────────────────────────────────────────────────── */}
      {view === 'day' && (
        <>
          <div style={{ display:'flex', alignItems:'center', gap:10, marginBottom:16, background:'var(--card)', borderRadius:10, padding:'10px 16px', border:'1px solid var(--border)' }}>
            <button className="btn btn-sm" onClick={() => shiftDate(-1)}>‹ Prev</button>
            <input type="date" value={date} max={today} onChange={e => setDate(e.target.value)} style={{ fontWeight:600, fontSize:14 }}/>
            <button className="btn btn-sm" onClick={() => shiftDate(1)} disabled={date >= today}>Next ›</button>
            <span style={{ fontSize:13, color:'var(--text2)', marginLeft:4 }}>{fmtDisp(date)}</span>
            {sunday && <span style={{ marginLeft:'auto', fontSize:12, fontWeight:600, color:'#555', background:'#f3f4f6', padding:'3px 10px', borderRadius:6 }}>Sunday — Weekly Off</span>}
          </div>

          {loadError && <ErrorBanner msg={loadError} onRetry={() => loadDay(date)}/>}

          {sunday ? (
            <div className="card">
              <div style={{ textAlign:'center', padding:'32px 20px', color:'var(--text2)' }}>
                <Calendar s={40} style={{ margin:'0 auto 12px', display:'block', opacity:0.25 }}/>
                <div style={{ fontSize:15, fontWeight:600 }}>Sunday is a weekly off day.</div>
                <div style={{ fontSize:13, marginTop:4 }}>No attendance needs to be marked for Sundays.</div>
              </div>
            </div>
          ) : (
            <>
              <div className="card card-0" style={{ marginBottom:12 }}>
                <table>
                  <thead>
                    <tr>
                      <th>Staff Member</th>
                      <th>Role</th>
                      <th style={{ width:360 }}>Attendance</th>
                      <th>Holiday / Note</th>
                    </tr>
                  </thead>
                  <tbody>
                    {staff.map(s => {
                      const cur = attendance[s.id] || 'present';
                      return (
                        <tr key={s.id}>
                          <td style={{ fontWeight:600 }}>{s.name}</td>
                          <td style={{ fontSize:12 }}>
                            <span className={`badge ${s.role==='admin'?'b-info':'b-gray'}`}>{s.role}</span>
                          </td>
                          <td>
                            <div style={{ display:'flex', gap:5 }}>
                              {STATUS_OPTIONS.map(opt => (
                                <button key={opt.value} onClick={() => setStatus(s.id, opt.value)}
                                  style={{ padding:'5px 11px', fontSize:12, fontWeight:600, borderRadius:6, border:'2px solid',
                                    borderColor:cur===opt.value?opt.color:'var(--border)',
                                    background: cur===opt.value?opt.bg:'transparent',
                                    color:      cur===opt.value?opt.color:'var(--text2)',
                                    cursor:'pointer', whiteSpace:'nowrap' }}>
                                  {opt.label}
                                </button>
                              ))}
                            </div>
                          </td>
                          <td>
                            {cur === 'holiday'
                              ? <input value={notes[s.id]||''} onChange={e => setNote(s.id, e.target.value)} placeholder="e.g. Hartal, Onam…" style={{ width:160, fontSize:12 }}/>
                              : <span style={{ fontSize:12, color:'var(--text2)' }}>—</span>
                            }
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
              <div style={{ display:'flex', justifyContent:'flex-end' }}>
                <button className="btn btn-primary" onClick={save} disabled={saving}>
                  {saving ? 'Saving…' : 'Save Attendance'}
                </button>
              </div>
            </>
          )}
        </>
      )}

      {/* ── Month View ────────────────────────────────────────────────────────── */}
      {view === 'month' && (
        <>
          <div style={{ display:'flex', alignItems:'center', gap:10, marginBottom:16, background:'var(--card)', borderRadius:10, padding:'10px 16px', border:'1px solid var(--border)' }}>
            <button className="btn btn-sm" onClick={() => { let m=viewMonth-1,y=viewYear; if(m<1){m=12;y--;} setViewMonth(m);setViewYear(y); }}>‹ Prev</button>
            <span style={{ fontWeight:700, fontSize:15 }}>{MN[viewMonth-1]} {viewYear}</span>
            <button className="btn btn-sm" onClick={() => { let m=viewMonth+1,y=viewYear; if(m>12){m=1;y++;} setViewMonth(m);setViewYear(y); }}>Next ›</button>
          </div>
          {monthError && <ErrorBanner msg={monthError} onRetry={() => loadMonth(viewYear, viewMonth)}/>}
          {monthBusy ? <Spin/> : <MonthGrid staff={staff} dataMap={monthMap} year={viewYear} month={viewMonth}/>}
        </>
      )}

      {/* ── Report View ───────────────────────────────────────────────────────── */}
      {view === 'report' && (
        <>
          {/* Config card */}
          <div className="card" style={{ padding:'18px 20px', marginBottom:16 }}>
            {/* Tab selector */}
            <div style={{ display:'flex', gap:8, marginBottom:16, paddingBottom:14, borderBottom:'1px solid var(--border)' }}>
              <button className={`btn btn-sm${rTab==='all'?' btn-primary':''}`}   onClick={() => { setRTab('all');   setRDone(false); }}>All Staff Report</button>
              <button className={`btn btn-sm${rTab==='staff'?' btn-primary':''}`} onClick={() => { setRTab('staff'); setRDone(false); }}>Staff Report</button>
            </div>

            {/* Staff selector */}
            {rTab === 'staff' && (
              <div style={{ marginBottom:14 }}>
                <label style={{ fontSize:12, fontWeight:600, color:'var(--text2)', display:'block', marginBottom:5 }}>
                  <User s={13} style={{ marginRight:4, verticalAlign:'middle' }}/>
                  Staff Member
                </label>
                <select value={rStaff} onChange={e => { setRStaff(e.target.value); setRDone(false); }} style={{ fontSize:13, minWidth:240 }}>
                  <option value="">— Select staff member —</option>
                  {staff.map(s => <option key={s.id} value={s.id}>{s.name} ({s.role})</option>)}
                </select>
              </div>
            )}

            {/* Mode picker */}
            <div style={{ display:'flex', gap:24, marginBottom:14 }}>
              <label style={{ display:'flex', alignItems:'center', gap:6, fontSize:13, cursor:'pointer' }}>
                <input type="radio" name="rmode" checked={rMode==='month'} onChange={() => { setRMode('month'); setRDone(false); }}/>
                By Month
              </label>
              <label style={{ display:'flex', alignItems:'center', gap:6, fontSize:13, cursor:'pointer' }}>
                <input type="radio" name="rmode" checked={rMode==='range'} onChange={() => { setRMode('range'); setRDone(false); }}/>
                Date Range
              </label>
            </div>

            {/* Period picker */}
            {rMode === 'month' ? (
              <div style={{ display:'flex', alignItems:'center', gap:8, marginBottom:16 }}>
                <button className="btn btn-sm" onClick={() => { let m=rMonth-1,y=rYear; if(m<1){m=12;y--;} setRMonth(m);setRYear(y);setRDone(false); }}>‹</button>
                <span style={{ fontWeight:700, minWidth:110, textAlign:'center', fontSize:15 }}>{MN[rMonth-1]} {rYear}</span>
                <button className="btn btn-sm" onClick={() => { let m=rMonth+1,y=rYear; if(m>12){m=1;y++;} setRMonth(m);setRYear(y);setRDone(false); }}>›</button>
              </div>
            ) : (
              <div style={{ display:'flex', alignItems:'flex-end', gap:16, marginBottom:16, flexWrap:'wrap' }}>
                <div>
                  <label style={{ fontSize:11, fontWeight:600, color:'var(--text2)', display:'block', marginBottom:4 }}>From</label>
                  <input type="date" value={rFrom} onChange={e => { setRFrom(e.target.value); setRDone(false); }} style={{ fontSize:13 }}/>
                </div>
                <div>
                  <label style={{ fontSize:11, fontWeight:600, color:'var(--text2)', display:'block', marginBottom:4 }}>To</label>
                  <input type="date" value={rTo} max={today} onChange={e => { setRTo(e.target.value); setRDone(false); }} style={{ fontSize:13 }}/>
                </div>
              </div>
            )}

            {rError && <ErrorBanner msg={rError}/>}

            <button className="btn btn-primary" onClick={loadReport} disabled={rBusy || (rTab==='staff' && !rStaff)}>
              {rBusy ? 'Loading…' : 'Generate Report'}
            </button>
          </div>

          {/* Results */}
          {rDone && !rBusy && (
            <>
              <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:12 }}>
                <div style={{ fontSize:14, fontWeight:600, color:'var(--text2)' }}>
                  {rTab === 'all' ? 'All Staff' : selStaffMember?.name}
                  {' — '}
                  {rMode === 'month' ? `${MNF[rMonth-1]} ${rYear}` : `${rFrom} to ${rTo}`}
                </div>
                <button className="btn btn-sm" style={{ display:'flex', alignItems:'center', gap:6 }}
                  onClick={rTab === 'all' ? handleDownloadAllPDF : handleDownloadStaffPDF}>
                  <Download s={14}/> Download PDF
                </button>
              </div>

              {/* All Staff — Month: full grid */}
              {rTab === 'all' && rMode === 'month' && (
                <>
                  <MonthGrid staff={staff} dataMap={rMap} year={rYear} month={rMonth}/>
                  <AttendanceSummary staffList={staff} rData={rData}/>
                </>
              )}

              {/* All Staff — Range: summary table */}
              {rTab === 'all' && rMode === 'range' && (() => {
                const sums = staff.map(s => {
                  let p = 0, a = 0, h = 0, o = 0;
                  rData.filter(r => r.staff_id === s.id).forEach(r => {
                    if (r.status === 'present')       p++;
                    else if (r.status === 'half_day') { h++; p += 0.5; }
                    else if (r.status === 'absent')   a++;
                    else if (r.status === 'holiday')  o++;
                  });
                  return { ...s, p, a, h, o };
                });
                return (
                  <>
                    <div className="card card-0">
                      <table>
                        <thead>
                          <tr>
                            <th style={{ textAlign:'left', padding:'8px 12px' }}>Staff Member</th>
                            <th style={{ textAlign:'center' }}>Full Day</th>
                            <th style={{ textAlign:'center' }}>Half Day</th>
                            <th style={{ textAlign:'center' }}>Absent</th>
                            <th style={{ textAlign:'center' }}>Extra OFF</th>
                          </tr>
                        </thead>
                        <tbody>
                          {sums.map(s => (
                            <tr key={s.id}>
                              <td style={{ fontWeight:600, padding:'8px 12px' }}>{s.name}</td>
                              <td style={{ textAlign:'center', fontWeight:700, color:'var(--green)' }}>{s.p}</td>
                              <td style={{ textAlign:'center', fontWeight:700, color:'#b45309' }}>{s.h}</td>
                              <td style={{ textAlign:'center', fontWeight:700, color:'var(--red)' }}>{s.a}</td>
                              <td style={{ textAlign:'center', fontWeight:700, color:'#7c3aed' }}>{s.o}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                    <AttendanceSummary staffList={staff} rData={rData}/>
                  </>
                );
              })()}

              {/* Staff Report */}
              {rTab === 'staff' && selStaffMember && (() => {
                const recs = rData.filter(r => r.staff_id === rStaff).sort((a,b) => a.date.localeCompare(b.date));
                let p = 0, a = 0, h = 0, o = 0;
                recs.forEach(r => {
                  if (r.status === 'present')       p++;
                  else if (r.status === 'half_day') { h++; p += 0.5; }
                  else if (r.status === 'absent')   a++;
                  else if (r.status === 'holiday')  o++;
                });
                return (
                  <div>
                    <div className="stat-grid" style={{ gridTemplateColumns:'repeat(4,1fr)', marginBottom:16 }}>
                      {[{label:'Full Day Present',val:p,color:'var(--green)'},{label:'Half Day',val:h,color:'#b45309'},{label:'Absent',val:a,color:'var(--red)'},{label:'Extra OFF',val:o,color:'#7c3aed'}].map(({ label, val, color }) => (
                        <div key={label} className="stat-card">
                          <div className="stat-lbl">{label}</div>
                          <div className="stat-val" style={{ color }}>{val}</div>
                        </div>
                      ))}
                    </div>
                    <div className="card card-0">
                      <table>
                        <thead>
                          <tr>
                            <th style={{ textAlign:'left', padding:'8px 12px' }}>Date</th>
                            <th style={{ textAlign:'center' }}>Day</th>
                            <th style={{ textAlign:'center' }}>Status</th>
                            <th style={{ textAlign:'left', padding:'8px 12px' }}>Note</th>
                          </tr>
                        </thead>
                        <tbody>
                          {recs.map(r => {
                            const d   = new Date(r.date+'T00:00:00');
                            const sty = STATUS_STYLE[r.status] || {};
                            return (
                              <tr key={r.id || r.date}>
                                <td style={{ fontWeight:500, padding:'7px 12px' }}>{d.toLocaleDateString('en-IN',{year:'numeric',month:'short',day:'numeric'})}</td>
                                <td style={{ fontSize:12, color:'var(--text2)', textAlign:'center' }}>{d.toLocaleDateString('en-IN',{weekday:'short'})}</td>
                                <td style={{ textAlign:'center' }}>
                                  <span style={{ background:sty.bg, color:sty.color, padding:'2px 12px', borderRadius:20, fontSize:12, fontWeight:600, whiteSpace:'nowrap' }}>
                                    {sty.label || r.status}
                                  </span>
                                </td>
                                <td style={{ fontSize:12, color:'var(--text2)', padding:'7px 12px' }}>{r.note || '—'}</td>
                              </tr>
                            );
                          })}
                          {recs.length === 0 && (
                            <tr><td colSpan="4" style={{ textAlign:'center', padding:24, color:'var(--text2)' }}>No attendance records found for this period.</td></tr>
                          )}
                        </tbody>
                      </table>
                    </div>
                  </div>
                );
              })()}
            </>
          )}
        </>
      )}
    </div>
  );
}
